// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { fontConfig } from "@steiner/XDL/PreStyle/typography";
import { avoidBreakInsidePrintElement, xScreen } from "@steiner/XDL/Utilities";
import { css } from "styled-components";

interface OrderInformationStyles {
    label?: TypographyProps;
    value?: TypographyProps;
    labelWithDots?: TypographyProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    address?: { heading?: TypographyProps; informationTitle?: TypographyProps } & AddressInfoDisplayStyles;
    note?: { wrapper?: InjectableCss; title?: TypographyProps; text?: TypographyProps };
    orderSummary?: InjectableCss;
    wrapper?: InjectableCss;
}

const xOrderInformationStyles: OrderInformationStyles = {
    label: {
        variant: "h6",
        css: css`
            margin-right: 4px;
            margin-bottom: 0;
            @media print {
                display: block;
            }
        `,
    },
    value: {
        variant: "h6",
        weight: fontConfig.weight.regular,
        css: css`
            margin-bottom: 0;
            width: auto;
            &:empty:after {
                content: "- -";
            }
            @media print {
                display: inline-block;
                width: auto;
            }
        `,
    },
    address: {
        informationTitle: {
            variant: "h3",
            as: "h2",
        },
        heading: {
            variant: "h6",
        },
    },
    note: {
        text: {
            variant: "h6",
            weight: fontConfig.weight.regular,
            css: css`
                margin-bottom: 0;
            `,
        },
    },
    orderSummary: {
        css: css`
            ${xScreen("lte", "md")} {
                margin-bottom: 10px;
            }
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            margin-bottom: 4px;
        `,
    },
};

xOrderInformationStyles.labelWithDots = {
    variant: "h6",
    transform: "none",
    css: css`
        margin-right: 4px;
        margin-bottom: 0;
        white-space: nowrap;
        &:after {
            content: ":";
        }
        @media print {
            display: inline-block;
            width: auto;
        }
    `,
};

xOrderInformationStyles.headingAndTextStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            row-gap: 4px;
            margin-bottom: 4px;
            align-items: center;

            @media print {
                display: block;
            }
        `,
    },
    heading: {
        ...xOrderInformationStyles.labelWithDots,
    },
    text: {
        ...xOrderInformationStyles.value,
    },
};

export default xOrderInformationStyles;
